import React, { useState, useEffect } from 'react';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { Dropzone, IMAGE_MIME_TYPE, PDF_MIME_TYPE } from '@mantine/dropzone';
import { Group, Text, Button, Stack } from '@mantine/core';
import { IconUpload, IconX, IconTrash } from '@tabler/icons-react';
import apiService, { API_BASE_URL } from '../services/api';
import './AdminMediaCenter.css';

export default function AdminMediaCenter() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('images');
  const [pendingFiles, setPendingFiles] = useState([]);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      const data = await apiService.getFiles();
      setFiles(data);
    } catch (err) {
      notifications.show({ title: 'Error', message: 'Failed to load files: ' + err.message, color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!pendingFiles.length) return;

    setUploading(true);
    const formData = new FormData();
    for (const file of pendingFiles) {
      formData.append('files', file);
    }

    try {
      await apiService.uploadFiles(formData);
      notifications.show({ title: 'Success', message: 'Files uploaded successfully!', color: 'green' });
      setPendingFiles([]);
      fetchFiles();
    } catch (error) {
      notifications.show({ title: 'Error', message: 'Upload failed: ' + error.message, color: 'red' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (id, filename) => {
    modals.openConfirmModal({
      title: 'Delete Media File',
      centered: true,
      children: <Text size="sm">Are you sure you want to delete <strong>{filename}</strong>?</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await apiService.deleteFile(id);
          fetchFiles();
        } catch (error) {
          notifications.show({ title: 'Error', message: 'Delete failed: ' + error.message, color: 'red' });
        }
      },
    });
  };

  const sizeToKB = (bytes) => (bytes / 1024).toFixed(1) + ' KB';

  if (loading) return <div role="status" aria-live="polite">Loading Media…</div>;

  return (
    <div className="admin-media-center">
      <h2>Media Center</h2>
      <p>Upload and manage images / videos (up to 20MB) for events, products, etc.</p>

      <Stack gap="md">
        <Dropzone
          onDrop={(dropFiles) => setPendingFiles((prev) => [...prev, ...dropFiles])}
          onReject={(rejectedFiles) => {
            notifications.show({
              title: 'Error',
              message: `Some files were rejected: ${rejectedFiles.map((f) => f.errors[0]?.message).join(', ')}`,
              color: 'red',
            });
          }}
          maxSize={20 * 1024 * 1024}
          accept={[...IMAGE_MIME_TYPE, ...PDF_MIME_TYPE, 'video/mp4']}
          loading={uploading}
        >
          <Group gap="xl" style={{ minHeight: 80, pointerEvents: 'none' }}>
            <IconUpload size={32} stroke={1.5} color="gray" aria-hidden="true" />
            <div>
              <Text size="sm" color="dimmed">
                Drag and drop files here, or click to select
              </Text>
              <Text size="xs" color="dimmed">
                Images, PDFs, and MP4 videos up to 20MB
              </Text>
            </div>
          </Group>
        </Dropzone>

        {pendingFiles.length > 0 && (
          <Stack gap="xs">
            {pendingFiles.map((file, idx) => (
              <Group key={idx} justify="space-between">
                <Text size="sm" truncate="end" style={{ maxWidth: 300 }}>
                  {file.name}
                </Text>
                <Button
                  variant="subtle"
                  color="red"
                  size="compact-xs"
                  leftSection={<IconX size={12} aria-hidden="true" />}
                  onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== idx))}
                  aria-label={`Remove ${file.name} from upload queue`}
                >
                  Remove
                </Button>
              </Group>
            ))}
            <Button
              leftSection={<IconUpload size={16} aria-hidden="true" />}
              onClick={handleUpload}
              loading={uploading}
              aria-label={`Upload ${pendingFiles.length} pending file${pendingFiles.length > 1 ? 's' : ''}`}
            >
              Upload {pendingFiles.length} File{pendingFiles.length > 1 ? 's' : ''}
            </Button>
          </Stack>
        )}
      </Stack>

      <div className="media-filters" role="tablist" aria-label="Media type filter">
        <Button
          variant={typeFilter === 'images' ? 'filled' : 'default'}
          size="sm"
          onClick={() => setTypeFilter('images')}
          aria-pressed={typeFilter === 'images'}
        >
          Images
        </Button>
        <Button
          variant={typeFilter === 'videos' ? 'filled' : 'default'}
          size="sm"
          onClick={() => setTypeFilter('videos')}
          aria-pressed={typeFilter === 'videos'}
        >
          Videos
        </Button>
        <Button
          variant={typeFilter === 'pdfs' ? 'filled' : 'default'}
          size="sm"
          onClick={() => setTypeFilter('pdfs')}
          aria-pressed={typeFilter === 'pdfs'}
        >
          PDFs
        </Button>
      </div>

      <div className="media-grid">
        {files
          .filter(f => {
            if (typeFilter === 'images') return f.mimetype?.startsWith('image/');
            if (typeFilter === 'videos') return f.mimetype?.startsWith('video/');
            if (typeFilter === 'pdfs') return f.mimetype === 'application/pdf';
            return true;
          })
          .map(f => (
          <div className="media-card" key={f.id}>
            <div className="media-preview">
              {f.mimetype && f.mimetype.startsWith('video') ? (
                <video src={`${API_BASE_URL}/files/${f.id}`} controls aria-label={`Video preview of ${f.filename}`} />
              ) : (
                <img src={`${API_BASE_URL}/files/${f.id}`} alt={f.filename} />
              )}
            </div>
            <div className="media-info">
              <span className="media-name" title={f.filename}>{f.filename}</span>
              <span className="media-size" aria-label={`Size: ${sizeToKB(f.size)}`}>{sizeToKB(f.size)}</span>
              <div className="media-actions">
                <Button variant="subtle" size="xs" onClick={() => {
                    navigator.clipboard.writeText(`${API_BASE_URL}/files/${f.id}`);
                    notifications.show({ title: 'Success', message: 'URL copied to clipboard', color: 'green' });
                  }}
                  aria-label={`Copy URL for ${f.filename}`}
                >
                  Copy URL
                </Button>
                <Button
                  variant="light"
                  color="red"
                  size="xs"
                  leftSection={<IconTrash size={14} aria-hidden="true" />}
                  onClick={() => handleDelete(f.id, f.filename)}
                  aria-label={`Delete ${f.filename}`}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {files.length === 0 ? <p className="empty-state">No media files uploaded yet.</p> : null}
    </div>
  );
}
